/**
 * Custom hook for HexMap state management.
 * Includes persistent viewport state via localStorage.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { Viewport, HexData } from './types';
import { hexKey } from '@/lib/hexMath';

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 2;
const ZOOM_STEP = 0.1;

// localStorage key for persisting viewport state
const VIEWPORT_STORAGE_KEY = 'sellune_map_viewport';

// Debounce delay for saving viewport to localStorage (ms)
const SAVE_DEBOUNCE_MS = 500;

/**
 * Default viewport state - centered on the map with zoom level 1.
 */
const DEFAULT_VIEWPORT: Viewport = {
    offsetX: 0,
    offsetY: 0,
    zoom: 1,
};

/**
 * Attempts to load saved viewport state from localStorage.
 * Returns the saved viewport if valid, otherwise returns the default viewport.
 */
function loadViewportFromStorage(): Viewport {
    try {
        if (typeof window === 'undefined' || !window.localStorage) {
            return DEFAULT_VIEWPORT;
        }

        const saved = localStorage.getItem(VIEWPORT_STORAGE_KEY);
        if (!saved) {
            return DEFAULT_VIEWPORT;
        }

        const parsed = JSON.parse(saved);

        // Validate the parsed data has the expected structure and types
        if (
            typeof parsed === 'object' &&
            parsed !== null &&
            typeof parsed.offsetX === 'number' &&
            typeof parsed.offsetY === 'number' &&
            typeof parsed.zoom === 'number' &&
            !isNaN(parsed.offsetX) &&
            !isNaN(parsed.offsetY) &&
            !isNaN(parsed.zoom) &&
            parsed.zoom >= MIN_ZOOM &&
            parsed.zoom <= MAX_ZOOM
        ) {
            return {
                offsetX: parsed.offsetX,
                offsetY: parsed.offsetY,
                zoom: parsed.zoom,
            };
        }

        // Invalid data - remove it and return default
        localStorage.removeItem(VIEWPORT_STORAGE_KEY);
        return DEFAULT_VIEWPORT;
    } catch {
        // JSON parse error or other issue - return default
        return DEFAULT_VIEWPORT;
    }
}

/**
 * Saves viewport state to localStorage.
 */
function saveViewportToStorage(viewport: Viewport): void {
    try {
        if (typeof window === 'undefined' || !window.localStorage) {
            return;
        }

        localStorage.setItem(VIEWPORT_STORAGE_KEY, JSON.stringify({
            offsetX: viewport.offsetX,
            offsetY: viewport.offsetY,
            zoom: viewport.zoom,
        }));
    } catch {
        // localStorage might be full or disabled - silently ignore
    }
}

export function useHexMap(hexes: HexData[]) {
    // Viewport state for pan/zoom - initialize from localStorage if available
    const [viewport, setViewport] = useState<Viewport>(loadViewportFromStorage);

    // Dragging state
    const [isDragging, setIsDragging] = useState(false);
    const dragStart = useRef({ x: 0, y: 0 });
    const viewportStart = useRef({ x: 0, y: 0 });

    // Ref for debounce timer to persist viewport to localStorage
    const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Debounced save of viewport state to localStorage
    // Saves 500ms after the user stops panning/zooming
    useEffect(() => {
        // Clear any existing timer
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }

        // Set a new timer to save after debounce delay
        saveTimerRef.current = setTimeout(() => {
            saveViewportToStorage(viewport);
        }, SAVE_DEBOUNCE_MS);

        // Cleanup timer on unmount or when viewport changes
        return () => {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }
        };
    }, [viewport]);

    // Create a map of hexes by coordinate for fast lookup
    const hexMap = useRef(new Map<string, HexData>());

    useEffect(() => {
        hexMap.current.clear();
        hexes.forEach((hex) => {
            hexMap.current.set(hexKey(hex), hex);
        });
    }, [hexes]);

    // Get hex at coordinate
    const getHexAt = useCallback((q: number, r: number): HexData | null => {
        return hexMap.current.get(hexKey({ q, r })) ?? null;
    }, []);

    // Pan handlers - Mouse events
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.button === 0) { // Left click
            setIsDragging(true);
            dragStart.current = { x: e.clientX, y: e.clientY };
            viewportStart.current = { x: viewport.offsetX, y: viewport.offsetY };
        }
    }, [viewport.offsetX, viewport.offsetY]);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (isDragging) {
            const dx = e.clientX - dragStart.current.x;
            const dy = e.clientY - dragStart.current.y;
            setViewport((v) => ({
                ...v,
                offsetX: viewportStart.current.x + dx,
                offsetY: viewportStart.current.y + dy,
            }));
        }
    }, [isDragging]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Pan handlers - Touch events for mobile
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        // Only handle single touch for panning
        if (e.touches.length === 1) {
            const touch = e.touches[0];
            setIsDragging(true);
            dragStart.current = { x: touch.clientX, y: touch.clientY };
            viewportStart.current = { x: viewport.offsetX, y: viewport.offsetY };
        }
    }, [viewport.offsetX, viewport.offsetY]);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (isDragging && e.touches.length === 1) {
            const touch = e.touches[0];
            const dx = touch.clientX - dragStart.current.x;
            const dy = touch.clientY - dragStart.current.y;
            setViewport((v) => ({
                ...v,
                offsetX: viewportStart.current.x + dx,
                offsetY: viewportStart.current.y + dy,
            }));
        }
    }, [isDragging]);

    const handleTouchEnd = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Zoom handlers
    const handleWheel = useCallback((e: React.WheelEvent) => {
        const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
        setViewport((v) => ({
            ...v,
            zoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, v.zoom + delta)),
        }));
    }, []);

    // Zoom controls
    const zoomIn = useCallback(() => {
        setViewport((v) => ({
            ...v,
            zoom: Math.min(MAX_ZOOM, v.zoom + ZOOM_STEP),
        }));
    }, []);

    const zoomOut = useCallback(() => {
        setViewport((v) => ({
            ...v,
            zoom: Math.max(MIN_ZOOM, v.zoom - ZOOM_STEP),
        }));
    }, []);

    const resetView = useCallback(() => {
        // Reset to default viewport and clear saved state
        setViewport(DEFAULT_VIEWPORT);
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                localStorage.removeItem(VIEWPORT_STORAGE_KEY);
            }
        } catch {
            // Silently ignore localStorage errors
        }
    }, []);

    return {
        viewport,
        isDragging,
        getHexAt,
        handlers: {
            // Mouse events for desktop
            onMouseDown: handleMouseDown,
            onMouseMove: handleMouseMove,
            onMouseUp: handleMouseUp,
            onMouseLeave: handleMouseUp,
            onWheel: handleWheel,
            // Touch events for mobile
            onTouchStart: handleTouchStart,
            onTouchMove: handleTouchMove,
            onTouchEnd: handleTouchEnd,
        },
        controls: {
            zoomIn,
            zoomOut,
            resetView,
        },
    };
}
